// Importing necessary libraries and hooks
import { useEffect, useState } from 'react';
import useStore from '@/store/useStore';
import useCoordinates from '@/hooks/useCoordinates';
import type { IDataShowroom } from '@/components/BuilderIO/common/CommonFields';
import beTwMerge from '@/utils/beTwMerge';
import { dataVirtual } from '@/utils/constants';
import SearchBar from '@/components/StoreLocator/SearchBar';

import { LoadingSpinner } from '../Loading';

// Defining the LocationPage component
export default function PageOne({
  user,
  info,
  builderShowroomsCopy,
  setBuilderShowroomsCopy,
  setInfo,
}: {
  user: any;
  info: any;
  builderShowroomsCopy: IDataShowroom[];
  setBuilderShowroomsCopy: React.Dispatch<React.SetStateAction<IDataShowroom[]>>;
  setInfo: (key: string, value: any) => void;
}) {
  // Using the state from the store
  const isMobile = useStore(state => state.isMobile);
  const filteredShowrooms = useStore(state => state.filteredAppointmentModalShowrooms);
  const setNearShowrooms = useStore(state => state.setNearAppointmentModalShowrooms);

  // Initializing state variables
  const [hasError, setHasError] = useState(false);
  const [firstLoaded, setFirstLoaded] = useState(false);
  const [errorSearchMessage, setErrorSearchMessage] = useState('');
  const [titleLocationsNear, setTitleLocationsNear] = useState(
    info.selectedShowroomStore.modalName === 'Virtual Appointment'
      ? 'Locations Near You'
      : `Locations Near ${info.selectedShowroomStore.modalName}`
  );

  // Mapping timezones
  const timezones = {
    PST: 'PT', // Pacific Standard Time
    PDT: 'PT', // Pacific Daylight Time
    EST: 'ET', // Eastern Standard Time
    EDT: 'ET', // Eastern Daylight Time
    CST: 'CT', // Central Standard Time
    CDT: 'CT', // Central Daylight Time
    MST: 'MT', // Mountain Standard Time
    MDT: 'MT', // Mountain Daylight Time
    AKST: 'AKT', // Alaska Standard Time
    AKDT: 'AKT', // Alaska Daylight Time
    HST: 'HST', // Hawaii-Aleutian Standard Time (Hawaii does not observe DST)
  };

  // Using the useCoordinates hook
  const { getCoordinates } = useCoordinates();

  // Function to update nearby showrooms
  /**
   * This function updates the list of nearby showrooms based on the provided zipcode, latitude, and longitude.
   * It sets the loading state to true, then checks if latitude and longitude are provided. If they are, it directly calls the setNearShowrooms function to fetch nearby showrooms.
   * If latitude and longitude are not provided but a zipcode is, it attempts to fetch the coordinates for the zipcode using the getCoordinates function.
   * If the coordinates are successfully fetched, it calls the setNearShowrooms function with the fetched coordinates. If an error occurs during the fetching process, it logs the error to the console.
   * If neither latitude, longitude, nor zipcode are provided, it defaults to using the user's IP latitude and longitude to fetch nearby showrooms.
   * After fetching the nearby showrooms, it updates the builderShowroomsCopy state with the new list of showrooms. Finally, it sets the loading state to false.
   *
   * @param {string} zipcode - The zipcode to use for fetching coordinates.
   * @param {number} latitude - The latitude to use for fetching nearby showrooms.
   * @param {number} longitude - The longitude to use for fetching nearby showrooms.
   */
  const updateNearShowrooms = async (zipcode, latitude, longitude) => {
    setInfo('locationsLoading', true);
    if (latitude && longitude) {
      const newFilteredShowrooms = await setNearShowrooms(latitude, longitude, '', '');

      setBuilderShowroomsCopy([...newFilteredShowrooms]);
    } else if (zipcode) {
      try {
        const newCoords = await getCoordinates(zipcode);

        const newFilteredShowrooms = await setNearShowrooms(newCoords?.lat, newCoords?.lng, newCoords?.state, '');

        setBuilderShowroomsCopy([...newFilteredShowrooms]);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
      }
    } else {
      try {
        const newFilteredShowrooms = await setNearShowrooms(user.ipLatitude, user.ipLongitude, null, '');

        setBuilderShowroomsCopy([...newFilteredShowrooms]);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
      }
    }
    setInfo('locationsLoading', false);
  };

  // useEffect hook to update nearby showrooms when the selected showroom changes
  useEffect(() => {
    if (!firstLoaded) {
      updateNearShowrooms(
        info.selectedShowroomStore.zipCode,
        info.selectedShowroomStore.latitude,
        info.selectedShowroomStore.longitude
      );
    }

    setFirstLoaded(true);
  }, [info.selectedShowroomStore]);

  // useEffect hook to update the builderShowroomsCopy when the filteredShowrooms change
  useEffect(() => {
    if (filteredShowrooms && filteredShowrooms.length > 0 && builderShowroomsCopy.length === 0) {
      setBuilderShowroomsCopy([...filteredShowrooms]);
    }
  }, [filteredShowrooms, builderShowroomsCopy, setBuilderShowroomsCopy]);

  // Function to handle the search input
  /**
   * This function handles the search input in the location page.
   * It updates the state with the new search input, sets the loading state to true,
   * and logs the search event in the dataLayer.
   * If the search input is empty, it checks if the selected showroom is not a virtual appointment.
   * If it is not a virtual appointment, it fetches the coordinates of the selected showroom's zip code,
   * sets the nearby showrooms, and updates the title of the locations near the selected showroom.
   * If the selected showroom is a virtual appointment, it sets the nearby showrooms to the user's location
   * and updates the title of the locations near the user.
   * If the search input is not empty, it fetches the coordinates of the search input, sets the nearby showrooms,
   * and updates the title of the locations near the search input.
   * If the search input is invalid, it sets an error message and the hasError state to true.
   * If the nearby showrooms are empty, it sets the nearby showrooms to an empty array and updates the title of the locations near the search input.
   * If the search input is valid and the nearby showrooms are not empty, it sets the nearby showrooms and updates the title of the locations near the search input.
   * @param {string} searchInput - The search input from the user.
   */
  const handleInputSearch = async (searchInput: string) => {
    (window as any).dataLayer.push({
      event: 'appointment_form',
      showroom_page_section: 'select a location',
      showroom_action: 'search',
      showroom_location: info.selectedShowroomStore.modalName || '',
      general_event_details: 'Undefined',
    });
    setInfo('locationsLoading', true);

    setHasError(false);
    setErrorSearchMessage('');
    const newTitle = searchInput ? `Locations Near ${searchInput}` : 'Locations Near You';

    if (!searchInput) {
      if (info.selectedShowroomStore.modalName !== 'Virtual Appointment') {
        const newCoords = await getCoordinates(info.selectedShowroomStore.zipCode);

        const filterList = await setNearShowrooms(newCoords?.lat, newCoords?.lng, newCoords?.state, '');
        const virtualIndex = filterList.findIndex(location => location.displayName1 === 'Virtual Appointment');
        const updatedList =
          virtualIndex !== -1
            ? [...filterList.slice(0, virtualIndex), ...filterList.slice(virtualIndex + 1)]
            : [...filterList];

        setBuilderShowroomsCopy([...(updatedList as any), dataVirtual]);
        setTitleLocationsNear(`Locations Near ${info.selectedShowroomStore.modalName}`);
      } else {
        const filterList = await setNearShowrooms(user.ipLatitude, user.ipLongitude, null, '');

        const virtualIndex = filterList.findIndex(location => location.displayName1 === 'Virtual Appointment');
        const updatedList =
          virtualIndex !== -1
            ? [...filterList.slice(0, virtualIndex), ...filterList.slice(virtualIndex + 1)]
            : [...filterList];

        setBuilderShowroomsCopy([...(updatedList as any), dataVirtual]);
        setTitleLocationsNear(newTitle);
      }

      setInfo('locationsLoading', false);
      return;
    }

    const coords = await getCoordinates(searchInput);
    let filterList: any[] = [];

    if (coords) {
      filterList = await setNearShowrooms(coords?.lat, coords?.lng, coords?.state, searchInput);
    } else {
      setErrorSearchMessage('Invalid City, State, or Zip Code.');
      setHasError(true);

      setInfo('locationsLoading', false);
      return;
    }

    if (!filterList || filterList?.length === 0) {
      setBuilderShowroomsCopy([]);
      setTitleLocationsNear(newTitle);

      setInfo('locationsLoading', false);
      return;
    }

    const virtualIndex = filterList.findIndex(location => location.displayName1 === 'Virtual Appointment');
    const updatedList =
      virtualIndex !== -1
        ? [...filterList.slice(0, virtualIndex), ...filterList.slice(virtualIndex + 1)]
        : [...filterList];

    setInfo('locationsLoading', false);
    setBuilderShowroomsCopy([...updatedList, dataVirtual]);
    setTitleLocationsNear(newTitle);
  };

  /**
   * Handles the selection of a location by the user.
   * This function updates the state with the selected showroom information,
   * sets the active page to the next step, and logs the selection event.
   *
   * @param {Object} showroom - The showroom object selected by the user.
   * It contains information such as the showroom's name, address, timezone, and ID.
   */
  const handleLocationSelection = showroom => {
    // Check if the selected showroom is different from the previously selected one
    if (
      showroom.setsterLocationId !== info.prevSelectedShowroomStore.setsterLocationId ||
      (info.selectedShowroomStore.setsterLocationId === 'Virtual Appointment' &&
        showroom.setsterLocationId !== 'Virtual Appointment')
    ) {
      // If the selected showroom is 'Virtual Appointment', update the state accordingly
      if (showroom.displayName1 === 'Virtual Appointment') {
        // Log the selection event for 'Virtual Appointment'
        (window as any).dataLayer.push({
          event: 'appointment_form',
          showroom_page_section: 'select a location',
          showroom_action: `select location`,
          showroom_location: `virtual`,
          general_event_details: 'Undefined',
        });

        // Update the state with 'Virtual Appointment' information
        setInfo('prevSelectedShowroomStore', dataVirtual);
        setInfo('selectedShowroomStore', dataVirtual);
        setInfo('selectedTimezoneId', '0');
        setInfo('selectedTime', null);
        setInfo('selectedDate', null);
        setInfo('selectedTimeId', null);
      } else {
        // Prepare the showroom information object
        const showroomInfo = {
          modalName: `${showroom?.displayName1}${showroom?.displayName2 ? ` - ${showroom?.displayName2}` : ''}`,
          displayName1: showroom?.displayName1 || '',
          displayName2: showroom?.displayName2 || '',
          addresLine1: showroom?.addressLine1 || '',
          addressLine2: showroom?.addressLine2 || '',
          timezoneName: showroom?.timezoneName || '',
          timezoneAbreviation: timezones[showroom?.timezoneAbreviation] || '',
          // timezoneId: showroom?.timezoneId || '',
          setsterLocationId: showroom.setsterLocationId || '',
          timezoneId: '0',
          id: showroom?.id || '',
          city: showroom?.city || '',
          state: showroom?.state || '',
          zipCode: showroom?.zipCode || '',
          latitude: showroom?.latitude || '',
          longitude: showroom?.longitude || '',
        };

        // Log the selection event for the physical showroom
        (window as any).dataLayer.push({
          event: 'appointment_form',
          showroom_page_section: 'select a location',
          showroom_action: `select location`,
          showroom_location: `${showroom?.displayName1}${showroom?.displayName2 ? ` - ${showroom?.displayName2}` : ''}`,
          general_event_details: 'Undefined',
        });

        // Update the state with the selected showroom information
        setInfo('selectedServiceName', '');
        setInfo('selectedDate', null);
        setInfo('selectedTime', null);
        setInfo('selectedTimeId', null);
        setInfo('selectedTimezone', '');
        setInfo('selectedTimezoneId', '0');
        setInfo('prevSelectedShowroomStore', showroomInfo);
        setInfo('selectedShowroomStore', showroomInfo);
        setInfo('setsterAvailabilityDateNightTimes', {});

        // Update nearby showrooms based on the selected showroom's location
        updateNearShowrooms(showroomInfo.zipCode, showroomInfo.latitude, showroomInfo.longitude);
      }
    }

    // Move to the next step in the appointment booking process
    setInfo('activePage', info.activePage + 1);
  };

  // Function to handle the virtual selection
  const handleVirtualSelection = () => {
    if (info.selectedShowroomStore.modalName !== 'Virtual Appointment') {
      setInfo('selectedTime', null);
      setInfo('selectedDate', null);
      setInfo('selectedTimeId', null);
    }
    setInfo('prevSelectedShowroomStore', dataVirtual);
    setInfo('selectedShowroomStore', dataVirtual);
    setInfo('selectedTimezoneId', '0');
    setInfo('activePage', info.activePage + 1);
  };

  // Rendering the LocationPage component
  return (
    <div className="tw-py-4 tw-px-4 tw-pb-6 tw-flex tw-flex-col tw-gap-y-4 lg:tw-gap-x-0 lg:tw-max-w-[742px] lg:tw-mx-auto">
      <h3 className="tw-uppercase tw-font-[500] tw-text-sm tw-text-center tw-m-0 lg:tw-text-lg tw-text-be-dark-gray tw-font-[Avenir] !tw-tracking-[0.7px] lg:!tw-tracking-[0.9px]">
        Select a location
      </h3>
      <div>
        <SearchBar
          placeholder="Search City, State, or Zip Code"
          className="min-[1200px]:tw-w-[706px]"
          onClick={handleInputSearch}
          hasError={hasError}
          isMobile={isMobile}
        />
        {/* Error message */}
        {hasError && (
          <div className="tw-w-full tw-flex tw-flex-row tw-font-book">
            <p className="tw-mb-0 tw-mt-1 tw-text-[11px] tw-text-error">{errorSearchMessage}</p>
          </div>
        )}
      </div>

      {info.locationsLoading ? (
        <div className="tw-flex tw-mt-12 tw-w-full tw-justify-center tw-col-span-2">
          <LoadingSpinner />
        </div>
      ) : (
        <div className="tw-flex tw-flex-col tw-gap-y-2">
          <h4 className="tw-m-0 tw-font-[500] tw-text-sm lg:tw-text-base tw-text-be-dark-gray">{titleLocationsNear}</h4>

          <div className="tw-flex tw-flex-col tw-gap-y-2">
            {builderShowroomsCopy.length > 0 ? (
              (info.selectedServiceName !== 'Drop Off' && info.selectedServiceName !== 'Pick Up'
                ? builderShowroomsCopy
                : builderShowroomsCopy.slice(0, builderShowroomsCopy.length - 1)
              ).map((showroom, index) => (
                <div
                  key={index}
                  role="button"
                  tabIndex={0}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      handleLocationSelection(showroom);
                    }
                  }}
                  className={beTwMerge(
                    'tw-px-4 tw-py-3 tw-cursor tw-border-solid tw-border-[1.5px] tw-cursor-pointer tw-border-[#D9D9D9] hover:tw-border-color-light-green-cta',
                    info.selectedShowroomStore.city === showroom.city &&
                      info.selectedShowroomStore.displayName1 === showroom.displayName1 &&
                      'tw-border-color-light-green-cta tw-border-[1.5px]'
                  )}
                  onClick={() => handleLocationSelection(showroom)}
                >
                  <h5 className="tw-m-0 tw-font-[500] tw-text-sm tw-text-be-dark-gray">
                    {showroom.displayName1}
                    {!!showroom.displayName2 && ` - ${showroom.displayName2}`}
                  </h5>
                  <p className="tw-text-color-grey-700 tw-m-0 tw-text-xs tw-font-book tw-font-[350]">
                    {showroom.displayName1 === 'Virtual Appointment' && 'Video Consultation'}
                    {showroom.addressLine1} {showroom.addressLine2} {showroom.city}
                    {showroom.state && `, ${showroom.state}`}
                    {showroom.zipCode && ` ${showroom.zipCode}`}
                  </p>
                </div>
              ))
            ) : (
              <div className="tw-mt-4">
                <p className="tw-font-[350] tw-font-book tw-text-sm">
                  We&apos;re sorry, there are no Brilliant Earth stores within 250 miles. Please try another search or
                  book a{' '}
                  <a
                    type="button"
                    className="tw-underline tw-cursor-pointer focus:tw-outline-none focus-visible:tw-outline-[-webkit-focus-ring-color]"
                    role="button"
                    tabIndex={0}
                    onKeyDown={e => {
                      if (e.key === 'Enter') {
                        handleVirtualSelection();
                      }
                    }}
                    onClick={() => {
                      handleVirtualSelection();
                    }}
                  >
                    Virtual Appointment
                  </a>
                  .
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
